import { i18n } from '@/plugins/i18n';
import { POSITION_DETAIL_TABS } from '../constants';
import { computed, Ref } from 'vue';
import {
  DAY_PER_MILLISECONDS,
  getStrategyLegsType,
  isPositionBuy,
  OPTION_REQUEST_STATE,
  STRATEGY_LEGS_TYPE,
} from '@core/option';
import useGetOptionContractStateLabel from './useGetOptionContractStateLabel';
import { getTimeDifference } from '@libs/utils';
import { ORDER_SIDES, useGetServerClock } from '@core/order';
import { useFeatureFlag } from '@libs/feature-flag';

type TfirstValue = {
  value: any;
  customClass?: any;
  round?: 'down' | 'up';
  icon?: string;
  prefix?: string;
  prefixClass?: any;
};

export type TPositionType = {
  label: string;
  firstValue: TfirstValue;
  secoundValue?: TfirstValue;
  type: 'quantity' | 'string' | 'dualValue' | 'number' | 'dualValuePercent';
  condition?: boolean;
};

export default function useGeneratePositionInfoDialogData(
  selectedTab: Ref<number>,
  position: any
) {
  const isStrategies = useFeatureFlag('strategies');

  const getDateLabel = ({
    isStart,
    isFinished,
    date,
  }: {
    isStart: boolean;
    isFinished: boolean;
    date: string;
  }) => {
    if (isStart) {
      return i18n.global.t('misc.today');
    } else if (isFinished) {
      return i18n.global.t('misc.finished');
    } else {
      return i18n.global.d(date, 'shortDate');
    }
  };

  const getCashRequestTypeLabel = (isMaximum: boolean) =>
    isMaximum
      ? i18n.global.t('option.settlementRequest.cashMaximum')
      : i18n.global.t('option.settlementRequest.cash');
  const getPhysicalRequestTypeLabel = (isMaximum: boolean) =>
    isMaximum
      ? i18n.global.t('option.settlementRequest.physicalMaximum')
      : i18n.global.t('option.settlementRequest.physical');

  const settlementRequestType = (item) => {
    if (
      item.physicalSettlementRequestQuantity === 0 &&
      item.cashSettlementRequestQuantity === 0
    ) {
      return i18n.global.t('option.settlementRequest.notRequested');
    }

    const cashSettlementType =
      item.cashSettlementRequestQuantity > 0
        ? getCashRequestTypeLabel(item.cashMaximumQuantity)
        : '';

    const physicalSettlementType =
      item.physicalSettlementRequestQuantity > 0
        ? getPhysicalRequestTypeLabel(item.physicalMaximumQuantity)
        : '';

    if (physicalSettlementType && cashSettlementType) {
      return cashSettlementType.concat(' - ', physicalSettlementType);
    }

    return physicalSettlementType ? physicalSettlementType : cashSettlementType;
  };

  const coveredQuantityShare = computed(() => {
    return (
      position.contractSize *
      (position.strategyQuantities?.coveredQuantity || 0)
    );
  });

  const isBuyContract = computed(
    () => position?.contractType === ORDER_SIDES.buy
  );

  const contractReturnValue = computed(() => {
    if (isBuyContract.value) {
      return (
        position?.underlyingAsseClosingPrice -
        position?.strikePrice * (position?.cashSettlementFee + 1)
      );
    } else {
      return (
        position?.strikePrice * (position?.cashSettlementFee + 1) -
        position?.underlyingAsseClosingPrice
      );
    }
  });

  const { time } = useGetServerClock();

  const generatePositionInfoDialogData = computed(() => {
    let data: TPositionType[] = [];

    if (selectedTab.value === POSITION_DETAIL_TABS.settlement.value) {
      data = [
        {
          label: i18n.global.t('option.cashSettlementDate'),
          firstValue: {
            value: getDateLabel({
              isStart: position.isInCashSettlementDay,
              isFinished: position.hasFinishedCashSettlement,
              date: position.cashSettlementDate,
            }),
          },
          type: 'string',
          condition: true,
        },
        {
          label: i18n.global.t('option.physicalSettlementDate'),
          firstValue: {
            value: getDateLabel({
              isStart: position.isInPhysicalSettlementDay,
              isFinished: position.hasFinishedPhysicalSettlement,
              date: position.physicalSettlementDate,
            }),
          },
          type: 'string',
          condition: true,
        },
        {
          label: i18n.global.t('option.settlementRequest.title'),
          firstValue: {
            value: settlementRequestType(position),
          },
          type: 'string',
          condition: true,
        },
        {
          label: i18n.global.t('option.physicalRequestStatus'),
          firstValue: {
            value: OPTION_REQUEST_STATE[position.physicalRequestState],
          },
          type: 'string',
          condition: position.physicalRequestState,
        },
        {
          label: i18n.global.t('option.physicalSettlementCondition.title'),
          firstValue: {
            value: i18n.global.t(
              'option.physicalSettlementCondition.settlementAtLossOrIndifferent'
            ),
          },
          type: 'string',
          condition: position.requestedInCaseOfOtmOrAtm,
        },
        {
          label: `${i18n.global.t(
            'option.physicalRequestedQuantity'
          )} (${i18n.global.t('misc.applied')})`,
          firstValue: { value: position.physicalSettlementQuantity },
          secoundValue: { value: position.physicalSettlementRequestQuantity },
          type: 'dualValue',
          condition: position.physicalSettlementRequestQuantity,
        },
        {
          label: i18n.global.t('option.cashRequestStatus'),
          firstValue: {
            value: OPTION_REQUEST_STATE[position.cashRequestState],
          },
          type: 'string',
          condition: position.cashRequestState,
        },
        {
          label: i18n.global.t('option.cashSettlementCondition.title'),
          firstValue: {
            value: i18n.global.t(
              'option.cashSettlementCondition.settlementByDeduction'
            ),
          },
          type: 'string',
          condition: position.shareInProportion,
        },
        {
          label: `${i18n.global.t(
            'option.cashRequestedQuantity'
          )}  (${i18n.global.t('misc.applied')})`,
          firstValue: {
            value: position.cashSettlementQuantity,
          },
          secoundValue: {
            value: position.cashSettlementRequestQuantity,
          },
          type: 'dualValue',
          condition: position.cashSettlementRequestQuantity,
        },
        {
          label: i18n.global.t('option.failedToDeliverQuantityTable'),
          firstValue: {
            value: position.failedToDeliverQuantity,
          },
          type: 'number',
          condition: position.failedToDeliverQuantity,
        },
      ];
    } else if (selectedTab.value === POSITION_DETAIL_TABS.performance.value) {
      data = [
        {
          label: i18n.global.t('modules.portfolio.net_average'),
          firstValue: {
            value: position.netAveragePrice,
            prefix: i18n.global.t('misc.rial'),
          },
          type: 'number',
          condition: true,
        },
        {
          label: i18n.global.t('modules.portfolio.break_point'),
          firstValue: {
            value: position.breakPoint,
            round: isBuyContract.value ? 'down' : 'up',
            prefix: i18n.global.t('misc.rial'),
          },
          type: 'number',
          condition: true,
        },
        {
          label: i18n.global.t('option.costPrice'),
          firstValue: {
            value:
              (!isPositionBuy(position.positionSide) ? '-' : '') +
              position?.costPrice,
            prefix: i18n.global.t('misc.rial'),
            round: 'up',
          },
          type: 'number',
          condition: true,
        },
        {
          label: i18n.global.t('option.remainingReturn'),
          firstValue: {
            value: position.returnValuePercentage / 100,
            customClass: {
              'text-neutral-primary': position.returnValuePercentage === 0,
              'text-critical-primary': position.returnValuePercentage < 0,
              'text-brand-primary': position.returnValuePercentage > 0,
            },
          },
          secoundValue: {
            value: position.returnValue,
            round: 'down',
            prefix: i18n.global.t('misc.rial'),
          },
          type: 'dualValuePercent',
          condition: true,
        },
        {
          label: i18n.global.t('option.strikeReturn'),
          firstValue: {
            value: position.strikeReturnValue,
            prefix: i18n.global.t('misc.rial'),
            round: 'down',
          },
          type: 'number',
          condition: true,
        },
      ];
    } else {
      data = [
        {
          label: i18n.global.t('option.openPositionsCount'),
          firstValue: {
            value: position.quantity,
            customClass: isPositionBuy(position.positionSide)
              ? 'bg-brand-secondary text-brand-primary'
              : 'bg-critical-secondary text-critical-primary',
          },
          type: 'quantity',
          condition: true,
        },
        {
          label: i18n.global.t('option.strikePrice'),
          firstValue: {
            value: position.strikePrice,
            prefix: i18n.global.t('misc.rial'),
          },
          type: 'number',
          condition: true,
        },
        {
          label: i18n.global.t('option.baseAssetPrice'),
          firstValue: {
            value:
              position?.underlyingAssetReferencePriceVariationPercentage / 100,
            customClass: {
              'text-brand-primary':
                position?.underlyingAssetReferencePriceVariationPercentage > 0,
              'text-critical-primary':
                position?.underlyingAssetReferencePriceVariationPercentage < 0,
            },
          },
          secoundValue: {
            value: position?.underlyingAssetLastTradePrice,
            prefix: i18n.global.t('misc.rial'),
          },
          type: 'dualValuePercent',
          condition: true,
        },
        {
          label: i18n.global.t('option.contractStatus'),
          firstValue: {
            value: useGetOptionContractStateLabel(contractReturnValue.value),
            customClass: {
              '!text-neutral-secondary !font-bold':
                contractReturnValue.value === 0,
              '!text-critical-secondary !font-bold':
                contractReturnValue.value < 0,
              '!text-brand-primary !font-bold': contractReturnValue.value > 0,
            },
          },
          type: 'string',
          condition: true,
        },
        {
          label: i18n.global.t('option.settlementDateDays'),
          firstValue: {
            value: `${
              getTimeDifference({
                startTime: new Date(time.value).getTime(),
                endTime:
                  new Date(
                    position.physicalSettlementDate as string
                  ).getTime() + DAY_PER_MILLISECONDS,
              }).differenceInDays
            } ${i18n.global.t('misc.day')}`,
          },
          type: 'string',
          condition: true,
        },
        {
          label: i18n.global.t('option.totalBlockedMargin'),
          firstValue: {
            value: position?.blockedMargin,
            prefix: i18n.global.t('misc.rial'),
          },
          type: 'number',
          condition: position?.blockedMargin > 0,
        },
        {
          label: i18n.global.t('option.guaranteeMethod'),
          firstValue: {
            value:
              STRATEGY_LEGS_TYPE[
                getStrategyLegsType(position?.strategyQuantities)
              ],
            customClass: '!font-bold',
          },
          type: 'string',
          condition:
            !!STRATEGY_LEGS_TYPE[
              getStrategyLegsType(position?.strategyQuantities)
            ],
        },
        {
          label: `${i18n.global.t('option.cashAmount')} (${
            position?.blockedMargin
          } ${i18n.global.t('misc.rial')})`,
          firstValue: {
            value: `${position?.strategyQuantities?.nakedQuantity}`,
            prefix: i18n.global.t('option.contract'),
            prefixClass: '!text-neutral-primary',
          },
          type: 'number',
          condition: position?.strategyQuantities?.nakedQuantity,
        },
        {
          label: `${i18n.global.t('option.baseAsset')} ${
            position?.underlyingAssetTitle
          } (${coveredQuantityShare.value} ${i18n.global.t(
            'modules.order.stock'
          )})`,
          firstValue: {
            value: `${position?.strategyQuantities?.coveredQuantity}`,
            prefix: i18n.global.t('option.contract'),
            prefixClass: '!text-neutral-primary',
          },
          type: 'number',
          condition: position?.strategyQuantities?.coveredQuantity,
        },
      ];
      position?.strategyQuantities?.spreadQuantities.map((spreadQuantity) => {
        const obj: TPositionType = {
          label: `${i18n.global.t('option.position')} (${
            spreadQuantity.instrumentName
          })`,
          firstValue: {
            value: spreadQuantity.quantity,
            prefix: i18n.global.t('option.contract'),
            prefixClass: '!text-neutral-primary',
          },
          type: 'number',
          condition: true,
        };
        data.push(obj);
      });

      if (position?.collateralQuantity && isStrategies) {
        data.push({
          label: i18n.global.t('modules.portfolio.collateralQuantityTitle'),
          firstValue: {
            value: position?.collateralQuantity,
            icon: 'NegarLockIcon',
          },
          type: 'string',
          condition: position?.collateralQuantity && isStrategies,
        });
      }
    }

    return data;
  });

  return { generatePositionInfoDialogData };
}
